import type { FishStockLog } from '../types/production'

import { toLocalCalendarDayKey } from './dateNormalization'
import { countInstantMs } from './countDayDeaths'
import { farmTimeZone } from './farmTime'

/**
 * Canonical daily fish-stock rollup for one (lot, calendar date) pair.
 *
 * Built by iterating every `FishStockLog.countedStock` whose `recordedAt`
 * resolves to the same calendar day and whose `lot` matches, then keeping
 * the LATEST value.
 *
 * Semantic difference from `dailyWeightRollup` (LATEST-VALUE-WINS vs
 * arithmetic mean):
 *
 * - `WeightSampleLog.weightGrams` values are measurements of the same
 *   population. Averaging multiple samples on the same day gives the best
 *   estimate — hence WSL uses an arithmetic mean.
 * - `FishStockLog.countedStock` values are competing snapshots of the same
 *   population, not measurements to average. If an operator counts the
 *   population twice on the same day, the SECOND count SUPERSEDES the first
 *   (later stock takes precedence). Same day = keep the count with the
 *   latest `recordedAt`; ties are broken by input order (later index wins),
 *   modelling "the most recently entered count is the current truth".
 *
 * v2.2 finding parallel to WSL: when any `FishStockLog` events exist for a
 * given `(lot, date)`, this rollup IS the canonical daily fish-stock signal.
 * Consumers should prefer `latestCountedStock` over any single event's
 * `countedStock` and over the `ProductionRecord.fishStock` field for that
 * day. Rows without any matching event fall back to the stored value.
 */
export type DailyFishStockRollup = {
  /** Calendar date key in `YYYY-MM-DD` form (farm-local interpretation). */
  date: string
  /** Trimmed lot label, or empty string when the source log omitted it. */
  lot: string
  /** Winning `countedStock` (latest-value-wins), integer-formatted as a string. */
  latestCountedStock: string
  /** Method of the winning log — surfaced so consumers can show a trust badge. */
  latestMethod: 'count' | 'estimation'
  /** IDs of every log that contributed, in input encounter order. */
  logIds: string[]
  /** Number of contributing logs. Equal to `logIds.length`. */
  eventCount: number
  /** Latest `recordedAt` value among contributing logs (raw ISO string). */
  latestRecordedAt: string
  /** Findings #203: the moment the winning count stands for (`countInstantMs`), or null. */
  latestCountAtMs: number | null
}

const toCalendarDateKey = (raw: string | undefined | null, timeZone?: string): string => {
  // Group by the *local* calendar day, matching the mortality + weight-sample
  // rollup contract and the finding #7 + #47 timezone rules. A
  // ProductionRecord stored as "2026-04-13T19:36" (datetime-local, no Z) and
  // its companion FishStockLog recorded as "2026-04-14T00:36:00.000Z" (UTC)
  // must land in the same bucket for operators in behind-UTC timezones.
  return toLocalCalendarDayKey(raw, timeZone)
}

const toLotKey = (raw: string | undefined | null): string => {
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

const parseRecordedAtMs = (raw: string | undefined | null): number | null => {
  if (typeof raw !== 'string' || !raw) return null
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : null
}

/** Compose the `(date, lot)` lookup key used by the rollup map. */
export const buildDailyFishStockRollupKey = (date: string, lot: string): string => {
  return `${date}::${lot}`
}

type WinnerState = {
  countedStock: number
  method: 'count' | 'estimation'
  recordedAtMs: number
  inputIndex: number
  recordedAtRaw: string
  countAtMs: number | null
}

/**
 * Group `FishStockLog` events by `(calendar date, lot)` and keep the
 * LATEST-VALUE-WINS `countedStock` per group.
 *
 * The returned map is keyed by `buildDailyFishStockRollupKey(date, lot)`.
 * Logs whose `recordedAt` cannot be parsed into a calendar date (or into a
 * millisecond timestamp) are skipped entirely — they cannot participate in a
 * "latest" comparison.
 *
 * Ties on `recordedAt` are broken by input order: the LATER index wins,
 * consistent with "the most recently entered count is the current truth".
 *
 * The function is pure and side-effect free. It does not mutate the input.
 */
export const rollupFishStockLogsByLotDay = (
  logs: readonly FishStockLog[]
): Map<string, DailyFishStockRollup> => {
  const result = new Map<string, DailyFishStockRollup>()
  const winners = new Map<string, WinnerState>()

  logs.forEach((log, index) => {
    // Findings #85: a record's day is its farm's day.
    const date = toCalendarDateKey(log.recordedAt, farmTimeZone(log.farmId))
    if (!date) return

    const recordedAtMs = parseRecordedAtMs(log.recordedAt)
    if (recordedAtMs === null) return

    const lot = toLotKey(log.lot)
    const key = buildDailyFishStockRollupKey(date, lot)
    const count = typeof log.countedStock === 'number' && Number.isFinite(log.countedStock)
      ? log.countedStock
      : 0
    const method: 'count' | 'estimation' = log.method === 'estimation' ? 'estimation' : 'count'
    const recordedAtRaw = typeof log.recordedAt === 'string' ? log.recordedAt : ''
    const countAtMs = countInstantMs(recordedAtRaw, farmTimeZone(log.farmId))

    const existing = result.get(key)
    const currentWinner = winners.get(key)

    if (!existing || !currentWinner) {
      const winner: WinnerState = {
        countedStock: count,
        method,
        recordedAtMs,
        inputIndex: index,
        recordedAtRaw,
        countAtMs
      }
      winners.set(key, winner)
      result.set(key, {
        date,
        lot,
        latestCountedStock: String(count),
        latestMethod: method,
        logIds: [log.id],
        eventCount: 1,
        latestRecordedAt: recordedAtRaw,
        latestCountAtMs: countAtMs
      })
      return
    }

    existing.logIds.push(log.id)
    existing.eventCount = existing.logIds.length

    // Latest-value-wins: later `recordedAt` supersedes; on tie, later input
    // index wins (models "most recently entered count is current truth").
    const supersedes = recordedAtMs > currentWinner.recordedAtMs
      || (recordedAtMs === currentWinner.recordedAtMs && index > currentWinner.inputIndex)

    if (supersedes) {
      currentWinner.countedStock = count
      currentWinner.method = method
      currentWinner.recordedAtMs = recordedAtMs
      currentWinner.inputIndex = index
      currentWinner.recordedAtRaw = recordedAtRaw
      currentWinner.countAtMs = countAtMs
      existing.latestCountedStock = String(count)
      existing.latestMethod = method
      existing.latestCountAtMs = countAtMs
    }

    if (recordedAtRaw && recordedAtRaw > existing.latestRecordedAt) {
      existing.latestRecordedAt = recordedAtRaw
    }
  })

  return result
}

/**
 * Convenience lookup: get the rollup that matches a `(rawDate, lot)` pair,
 * normalizing the date the same way as `rollupFishStockLogsByLotDay`.
 */
export const findDailyFishStockRollup = (
  rollup: ReadonlyMap<string, DailyFishStockRollup>,
  rawDate: string,
  lot: string | undefined | null
): DailyFishStockRollup | undefined => {
  const date = toCalendarDateKey(rawDate)
  if (!date) return undefined
  return rollup.get(buildDailyFishStockRollupKey(date, toLotKey(lot)))
}
